// Phone layout (sprint 7, roadmap 8). A portrait window narrower than the 640 stage gets its own one-bit layout,
// still at 2x (so every sentence keeps its art-bible size), as wide as the window: menu bar, a close-up of the
// door glass, tonight's rule taped across it, the chat window with the round's controls, then "This shift" below
// (scroll down). Landscape phones and every desktop window keep the desktop layout (Ruling, studio/sprints/7).
// Pure: no DOM. scene-onebit.js draws into these rectangles; test/phone-layout.test.js checks them.
import { STAGE_W, MIN_SCALE } from './scale.js';
import { GLASS } from './visitors.js';

export const PHONE = {
  scale: MIN_SCALE, // 2x, never less (studio/ART-BIBLE.md)
  touch: 22,        // logical px: 44 px on screen, the smallest touch target
  menuH: 22,        // the menu bar is one touch row
  minW: 160,        // a 320 px window; the glass (180) is then cropped at its sides
  // the shortest play area: menu, minGlass, the folded rule, chatLow. Safari on a 390x844 iPhone with its bars
  // showing (390x664, 311 rows) fits exactly, so Open and Keep shut are on screen there; a shorter window scrolls
  minPlayH: 311,
  shortPlayH: 340,  // a play area up to this high (390x664 is 311 rows) gets the compact start dialog, so its buttons are all on screen
  hourH: 186,      // "This shift", under the play area (no disclaimer in it: the strip is always on a phone)
  noteOverlap: 30,  // tonight's rule is taped over the bottom of the glass, over the visitor's chest
  // Who gets the height, in order: the chat window down to chatLow (room for your last question and its reply),
  // the glass up to glassMid (head and shoulders), the chat up to chatMin, then the glass up to the whole arch
  chatLow: 170,
  glassMid: 96,
  chatMin: 194,
  minGlass: 96,     // glass rows always shown above the rule: head and shoulders
  fmGlass: 56,      // the first minute never folds its rule, so on a short phone the glass gives way instead (the head still shows)
                    // and the chat keeps room for the visitor's reply under your question
  fmChat: 14,       // extra chat rows the first minute asks for (its question above the reply), taken from the glass down to fmGlass
  maxCrop: 30,      // at most this much of the arch's top goes under the menu bar; the rest goes under the rule
  fmCrop: 46,       // the first minute's short glass crops more of the arch, so the visitor's eyes clear the rule
  // a short phone (Safari with its bars, 390x664) cannot fit head and shoulders, tonight's rule and a question
  // with its reply: the rule folds into a one-line strip that opens on tap (scene-onebit.js), drawn over the glass
  foldH: 16,
  foldGap: 4,
  // chat bubbles (scene-onebit.js): a bubble is its lines plus pad rows high; the gap under each, by kind
  bubble: { pad: 6, gap: { me: 7, them: 9, sys: 5, err: 5, typing: 8 } },
};
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

export const isPhone = (vw, vh) => vw < STAGE_W && vh > vw;

// The window's size as the visitor sees it. A mobile browser grows the layout viewport (innerWidth 1280 on a 390 px phone) when
// the page is wider than the screen; then the device and the visual viewport tell the truth. Desktop windows and landscape phones
// (never wider than the device's long side) are read as they are.
export function viewSize(g = globalThis) {
  const { innerWidth: w, innerHeight: h, screen: s, visualViewport: v } = g;
  if (!s || w <= Math.max(s.width, s.height)) return [w, h];
  return [Math.min(v?.width ?? w, s.width), Math.min(v?.height ?? h, Math.max(s.width, s.height))];
}

// stripPx: the height of the fixed disclaimer strip, which covers the bottom of the window
export const phoneSize = (vw, vh, stripPx = 0) => ({
  w: Math.max(PHONE.minW, Math.floor(vw / PHONE.scale)),
  h: Math.max(PHONE.minPlayH, Math.floor((vh - stripPx) / PHONE.scale)),
});

// The rectangles for one frame, in logical pixels. w, playH: from phoneSize; noteH: the rule note's height
// (0 when there is no rule yet). Every control is PHONE.touch high.
// A short window shows less glass (down to minGlass rows, head and shoulders, cropped up to maxCrop at the top,
// the rest under the rule) so the chat keeps room for a question and its reply; a tall one shows the whole arch
// above the rule. When even that does not fit, the rule folds to a one-line strip (note.folded; note.full is
// the unfolded height, which opens over the glass on tap).
// keepOpen: never fold the rule (the first minute shows its one-line rule in full)
// withHour: false while the night shift is off; the page then ends with the play area, so nothing scrolls
export function phoneFrame(w, playH, noteH = 0, keepOpen = false, withHour = true) {
  const T = PHONE.touch, M = PHONE.menuH, full = GLASS.h - PHONE.noteOverlap;
  const room = n => playH - M - 1 - n - 2; // glass rows + the chat window's height, under a note taking n rows
  const folded = !keepOpen && noteH > 0 && room(noteH + 5) - PHONE.chatLow < PHONE.minGlass;
  const noteRows = folded ? PHONE.foldH + PHONE.foldGap : noteH ? noteH + 5 : 4;
  const avail = room(noteRows);
  const low = PHONE.chatLow + (keepOpen ? PHONE.fmChat : 0); // the first minute's chat also holds the player's question above the reply
  const vis = clamp(Math.max(Math.min(avail - low, PHONE.glassMid), avail - PHONE.chatMin), keepOpen ? PHONE.fmGlass : PHONE.minGlass, full);
  const crop = Math.min(keepOpen ? PHONE.fmCrop : PHONE.maxCrop, Math.round((full - vis) * 0.6));
  const glass = { x: Math.round((w - GLASS.w) / 2), y: M + 1 - crop, w: GLASS.w, h: GLASS.h, shown: vis };
  const door = { x: glass.x - 24, y: glass.y - 16, w: 228, h: 295 }; // the desktop door, seen close up
  const note = { x: 4, y: M + 1 + vis, w: w - 8, h: folded ? PHONE.foldH : noteH, folded, full: noteH };
  const cy = note.y + noteRows;
  const chat = { x: 4, y: cy, w: w - 8, h: playH - cy - 2 };
  // inside the chat window: the timer is a bar under the title; from the bottom up, the decision buttons,
  // the question chips, the talk field; the messages fill the rest
  const timer = { y: chat.y + 17, h: 4 };
  const buttons = { y: chat.y + chat.h - T - 5, h: T };
  const chips = { y: buttons.y - 6 - T, h: T }; // 6: Keep shut's 4 px default ring, then 2 rows of paper
  const ask = { y: chips.y - 4 - T, h: T };
  // the messages, newest at the bottom; the typing dots sit in the title bar, not in here
  const msgs = { y: timer.y + timer.h + 3, h: ask.y - 5 - (timer.y + timer.h + 3) };
  const half = Math.floor((chat.w - 24 - 12) / 2);
  const open = { x: chat.x + 12, w: half }, shut = { x: chat.x + chat.w - 12 - half, w: half };
  const hour = withHour ? { x: 4, y: playH + 4, w: w - 8, h: PHONE.hourH } : null;
  // a practice lesson's one-row header, over the top of the glass (above every visitor's eyes: see plateY)
  const coach = { x: 4, y: M + 1, w: w - 8, h: T };
  return { w, h: hour ? hour.y + hour.h + 6 : playH, playH, menuH: M, touch: T, glass, door, note, coach, chat, buttons, timer, chips, ask, msgs, open, shut, hour };
}

// Where the event plate (Gold knock, Red knock, Outbreak) sits on the glass: never under the menu bar, and in a
// lesson never under its header row.
// tray: the envelope tray's chips (Lamp, Keyring, Session; client/mail.css: 48 px of padding, a 28 px chip, its 2 px shadow) are up, so the plate starts under them.
export const TRAY_BOTTOM = (48 + 28 + 2) / 2 + 1;
// tray: true for the one-row tray, or the measured bottom of the tray in rows (it wraps to two rows when the lamp is lit; the scene measures it).
export const plateY = (f, lesson = false, tray = false) => Math.max(f.glass.y + 10, f.menuH + 4, lesson ? f.coach.y + f.coach.h + 1 : 0, tray === true ? TRAY_BOTTOM : tray || 0);

// The envelope's window (client/mail.css, sprint 28 item 3) on a phone, placed from this layout's own numbers (sprint 29 item 2).
// It sits over the door glass, between the menu bar and the sum chalked where the rule hangs, and must leave the whole sum and the
// chat's title bar (the visitor's name) clear, ring and shadow included. Everything here is in CSS px.
//   full: the sprint 28 window, 196 high at top 80 with the tray above it, when it ends (gilded shadow and a gap included) above the sum.
//   short (Safari with its bars, 390 x 664): the tray's chips are hidden while the window is up (they never ride over the menu bar, sprint 31), the window starts just under the
//   menu bar, its rings lose their offset shadow, its words move into its title bar, and its scene is cropped (never scaled: still 2 or
//   3 px per art px) to what fits above the sum. a: how far the 2x view is moved up (the gap and the envelope kept in view); b: the same
//   for the 3x view (the opened envelope kept in view).
export const MAIL_WIN = { top: 80, h: 196, chrome: 28, scene: 168, ring: 8, below: 16, belowShort: 10, minScene: 80 };
export function mailFit(sumTop, menuBottom = (PHONE.menuH + 1) * PHONE.scale) {
  const M = MAIL_WIN;
  if (!Number.isFinite(sumTop) || M.top + M.h + M.below <= sumTop) return { short: false, top: M.top, sceneH: M.scene, a: 0, b: 0 };
  const top = menuBottom + M.ring;
  const sceneH = Math.max(M.minScene, Math.min(M.scene, 2 * Math.floor((sumTop - M.belowShort - top - M.chrome) / 2)));
  const a = Math.max(0, Math.min(M.scene - sceneH, Math.round(118 - sceneH / 2))); // 118: the envelope's middle at 2x
  const b = Math.max(0, Math.min(252 - 96 - sceneH, Math.round(63 - sceneH / 2))); // 63: the opened envelope's middle at 3x (ZOOM.y -96)
  return { short: true, top, sceneH, a, b };
}

// The same on the desktop layout (and a landscape phone): the window sits on the door, so it goes under the sum chalked on the door when it
// fits the height, else above it, else (no room either way) where mail.css puts it (null). sum: the slab's { t, b } in CSS px; vh: the
// window's height; menuBottom: the menu bar's foot. up: how far the gilded burst reaches above the window; down: the window, its gilded ring
// and shadow below its top.
export const MAIL_DESK = { up: 36, down: 212, gap: 2 };
export function mailFitDesk(sum, vh, menuBottom = 36) {
  if (!sum || !Number.isFinite(sum.t) || !Number.isFinite(sum.b)) return null;
  const { up, down, gap } = MAIL_DESK;
  const below = Math.ceil(sum.b + gap + up);
  if (below + down <= vh) return below;
  const above = Math.floor(sum.t - gap - down);
  if (above - up >= menuBottom) return above;
  return null;
}

// The Tonight entry on a phone (sprint 29 item 2): a button in the start dialog's brand row, beside "Knock / Who's there?", so it is on
// the first screen (the link row under the dialog is below the fold at 390 x 844). Two lines from the link's words (client/ui.js):
// "Tonight #7" over "5 doors", or over what follows its comma ("door 3", "done"). doors: game/tonight.js TONIGHT.doors.
export function tonightLines(text, doors = 5) {
  const t = String(text ?? '').replace(/\s+/g, ' ').trim();
  if (!t) return null;
  const i = t.indexOf(', ');
  return i < 0 ? [t, `${doors} doors`] : [t.slice(0, i), t.slice(i + 2)];
}

// The verdict alert's two buttons, x relative to the alert (AW wide): Skip on the left, Next knock on the right
// with its 4 px default ring, and at least 4 px of paper between Skip and that ring even on the narrowest phone.
export function verdictButtons(AW) {
  const skip = { x: 6, w: 44 }, w = Math.min(96, AW - 74);
  return { skip, next: { x: AW - 16 - w, w } };
}
